import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter(
    (a) => a.category === slug
  );

  const pageTitle = currentCategory
    ? `${currentCategory.name} - ${siteInfo.name}`
    : `${t('all_categories')} - ${siteInfo.name}`;

  const pageDesc = currentCategory
    ? currentCategory.description
    : siteInfo.description;

  const breadcrumbItems = [
    { label: t('breadcrumb_home'), href: '/' },
    { label: currentCategory ? currentCategory.name : t('all_categories') },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `${siteInfo.url}${item.href}` : undefined,
    })),
  };

  return (
    <>
      <SEO
        title={currentCategory?.name || t('all_categories')}
        description={pageDesc}
        keywords={`${currentCategory?.name || '伦理学习'},伦理学,道德教育,${siteInfo.name}`}
        canonicalUrl={`${siteInfo.url}/category/${slug || ''}`}
        schema={breadcrumbSchema}
      />

      <main className="pt-16 md:pt-18">
        <div className="w-full px-4 md:px-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <div className="pb-6 md:pb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {currentCategory ? currentCategory.name : t('all_categories')}
              </h1>
              {currentCategory && (
                <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                  {currentCategory.description}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50 border-b border-background-200/70">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to="/category/ethical-theory"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  slug === 'ethical-theory'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                伦理理论
              </Link>
              <Link
                to="/category/applied-ethics"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  slug === 'applied-ethics'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                应用伦理
              </Link>
              <Link
                to="/category/professional-ethics"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  slug === 'professional-ethics'
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                职业伦理
              </Link>
              <Link
                to="/category/case-analysis"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  slug === 'case-analysis'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                案例分析
              </Link>
              <Link
                to="/category/ethics-assessment"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  slug === 'ethics-assessment'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                伦理测评
              </Link>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-6xl mx-auto">
            {categoryArticles.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-6 md:mb-8">
                  <p className="text-sm text-foreground-600">
                    共 {categoryArticles.length} {t('articles_count')}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard
                      key={article.id}
                      slug={article.slug}
                      title={article.title}
                      summary={article.summary}
                      category={article.category}
                      author={article.author}
                      date={article.date}
                      readTime={article.readTime}
                      coverImage={article.coverImage}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">{t('no_articles')}</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}